import * as React from 'react';
import { observer } from 'mobx-react';
import { ConfigViews } from 'typings/enums';
import { navigateToNoMst, cycleRules } from 'utils/MobxDataManager/singletons';
import { formatDate, dateTextFormat } from 'utils/dateUtils';
import { setConvertedCycleSettingsViewCycle } from 'nomst/Actions/configurations';
import './cycleConfigRow.scss';
import { Configuration } from 'nomst/Stores/Configuration/configuration';

export interface ICycleConfigRowProps {
    config: Configuration;
    selectedCycleId: number;
    currentView: ConfigViews;
}

export class CycleConfigRow extends React.Component<ICycleConfigRowProps> {
    constructor(props: ICycleConfigRowProps) {
        super(props);
        this.handleOpenBudgetConfig = this.handleOpenBudgetConfig.bind(this);
        this.handleOpenWorkflowConfig = this.handleOpenWorkflowConfig.bind(this);
        this.handleOpenCycleConfig = this.handleOpenCycleConfig.bind(this);
    }

    render(): React.ReactNode {
        const { config } = this.props;
        const selectedCycleClass = this.isSelectedCycle() ? 'cycle-config-row--is-selected' : '';
        return (
            <li
                className={`cycle-config-row ${selectedCycleClass}`}
            >
                <div className="flex-row">
                    <i className="icon-calendar" />
                    <button className="cycle-name pxl-link" onClick={this.handleOpenBudgetConfig}>{this.getCycleName()}
                        <span className="budget-fields-text">{this.getBudgetFieldsText()}</span>
                    </button>
                    {this.renderCycleConfigurationTag()}
                </div>
                <ul hidden={!this.isSelectedCycle()}>
                    {!cycleRules.isCycleClosedForId(config.cycleId) &&
                    (
                        <li>
                            <button
                                className={`cycle-config-row__budget-setting ${this
                                    .selectedConfigClass(ConfigViews.Budget)} pxl-link`}
                                onClick={this.handleOpenBudgetConfig}
                            >Budgets
                            </button>
                        </li>
                    )}
                    {!cycleRules.isCycleClosedForId(config.cycleId) &&
                    (
                        <li>
                            <button
                                className={`cycle-config-row__workflow-settings ${this
                                    .selectedConfigClass(ConfigViews.Workflow)}  pxl-link`}
                                onClick={this.handleOpenWorkflowConfig}
                            >Workflow Settings
                            </button>
                        </li>
                    )}
                    <li>
                        <button
                            className={`cycle-config-row__cycle-settings ${this
                                .selectedConfigClass(ConfigViews.Cycle)} pxl-link`}
                            onClick={this.handleOpenCycleConfig}
                        >Cycle Settings
                        </button>
                    </li>
                </ul>
            </li>
        );
    }

    private renderCycleConfigurationTag(): React.ReactNode {
        const { config } = this.props;

        if (config.isInPreviewMode) {
            return (
                <span
                    className="active-cycle active-cycle--preview"
                >
                    Preview
                </span>
            );
        }

        if (config.isOpen) {
            return (
                <span
                    className="active-cycle active-cycle--open"
                >
                    Open
                </span>
            );
        }

        if (config.isClosing || config.isClosed || config.isPublished) {
            return (
                <span
                    className="active-cycle active-cycle--closed"
                >
                    Closed
                </span>
            );
        }

        return null;
    }

    private getCycleName(): string {
        const { config } = this.props;
        return `${formatDate(config.payEffectiveDate, dateTextFormat)} Cycle`;
    }

    private getBudgetFieldsText(): string {
        const { config } = this.props;
        if (!config.cycleBudgetFields) {
            return '';
        }
        return config.cycleBudgetFields.map(field => field.title).join(', ');
    }

    private isSelectedCycle(): boolean {
        const { config, selectedCycleId } = this.props;
        return config.cycleId === selectedCycleId;
    }

    private selectedConfigClass(configView: ConfigViews): string {
        if (this.isSelectedCycle() && configView === this.props.currentView) {
            return 'cycle-config-row__setting--is-selected';
        }

        return '';
    }

    private async handleOpenBudgetConfig(): Promise<void> {
        setConvertedCycleSettingsViewCycle(this.props.config.cycleId);
        cycleRules.isCycleClosedForId(this.props.config.cycleId) ?
            await navigateToNoMst.navigateToCycleSettingsView(this.props.config.cycleId) :
            navigateToNoMst.navigateToMstBudgetSettings(this.props.config.cycleId);
    }

    private async handleOpenWorkflowConfig(): Promise<void> {
        cycleRules.isCycleClosedForId(this.props.config.cycleId) ?
            await navigateToNoMst.navigateToCycleSettingsView(this.props.config.cycleId) :
            navigateToNoMst.navigateToMstWorkflowSettings(this.props.config.cycleId);
    }

    private async handleOpenCycleConfig(): Promise<void> {
        await navigateToNoMst.navigateToCycleSettingsView(this.props.config.cycleId);
    }
}

export default observer(CycleConfigRow);